import React, { useEffect, useState, useRef } from 'react';
import { Layer, Circle, Text, Group } from 'react-konva';
import { CELL_SIZE } from './GridLayer';
import { useCampaign } from '../../contexts/CampaignContext';

const PING_LIFE_MS    = 5000;  // fully fade by this age
const PING_CLEANUP_MS = 10000; // delete from Firestore past this age
const PULSE_PERIOD_MS = 1200;  // one pulse every 1.2s

export default function PingLayer({ bare = false }) {
  const { pings, players, tokens, deletePing } = useCampaign();
  const [, setTick] = useState(0);
  const cleanedRef = useRef(new Set());
  // Track when THIS device first saw each ping. Using ping.createdAt
  // directly fails across devices with clock skew (an iPhone whose clock
  // is even 2s off shows brand-new pings as already expired).
  const seenAtRef = useRef(new Map());

  // Drive animation via 50ms interval re-render — but only while pings exist.
  // Previously this ran 20x/sec forever, forcing a constant re-render on every
  // device even with zero pings on the map.
  const hasPings = Object.keys(pings).length > 0;
  useEffect(() => {
    if (!hasPings) return undefined;
    const id = setInterval(() => setTick((t) => (t + 1) % 1000000), 50);
    return () => clearInterval(id);
  }, [hasPings]);

  // Record local first-sight for any newly arrived ping, and drop entries
  // from the map for pings that no longer exist.
  useEffect(() => {
    const now = Date.now();
    const seen = seenAtRef.current;
    for (const ping of Object.values(pings)) {
      if (!seen.has(ping.id)) seen.set(ping.id, now);
    }
    for (const id of [...seen.keys()]) {
      if (!pings[id]) seen.delete(id);
    }
  }, [pings]);

  // Cleanup expired pings (any client can do it). Use seenAt so this
  // matches the render lifetime.
  useEffect(() => {
    const now = Date.now();
    for (const ping of Object.values(pings)) {
      const seenAt = seenAtRef.current.get(ping.id) ?? now;
      const age = now - seenAt;
      if (age > PING_CLEANUP_MS && !cleanedRef.current.has(ping.id)) {
        cleanedRef.current.add(ping.id);
        deletePing(ping.id).catch(() => { cleanedRef.current.delete(ping.id); });
      }
    }
  }, [pings, deletePing]);

  const pingList = Object.values(pings);
  if (pingList.length === 0) return bare ? null : <Layer listening={false} />;

  const now = Date.now();

  const body = (
    <>
      {pingList.map((ping) => {
        const seenAt = seenAtRef.current.get(ping.id) ?? now;
        const age = now - seenAt;
        if (age < 0 || age > PING_LIFE_MS) return null;

        const t          = age / PING_LIFE_MS;           // 0..1 lifetime
        const fade       = 1 - t;                        // 1..0 fade out
        const pulsePhase = (age % PULSE_PERIOD_MS) / PULSE_PERIOD_MS; // 0..1
        const pulseR     = 0.3 + pulsePhase * 0.7;       // outer ring grows

        const cx = (ping.x + 0.5) * CELL_SIZE;
        const cy = (ping.y + 0.5) * CELL_SIZE;
        const colour = ping.colour || '#60a5fa';

        // Find player name / char name for label
        const player = players?.[ping.playerId];
        const token  = tokens ? Object.values(tokens).find((tk) => tk.ownerId === ping.playerId) : null;
        const label  = token?.name || player?.charName || player?.name || '';

        return (
          <Group key={ping.id}>
            {/* Expanding pulse ring */}
            <Circle
              x={cx} y={cy}
              radius={pulseR * CELL_SIZE}
              stroke={colour}
              strokeWidth={3}
              opacity={fade * (1 - pulsePhase) * 0.7}
            />
            {/* Inner solid dot */}
            <Circle
              x={cx} y={cy}
              radius={CELL_SIZE * 0.18}
              fill={colour}
              opacity={fade * 0.9}
              shadowColor={colour}
              shadowBlur={14}
              shadowOpacity={fade}
            />
            {/* Outer stroke ring */}
            <Circle
              x={cx} y={cy}
              radius={CELL_SIZE * 0.3}
              stroke={colour}
              strokeWidth={2.5}
              opacity={fade}
            />
            {/* Label */}
            {label && (
              <Text
                x={cx - 60}
                y={cy - CELL_SIZE * 0.6 - 14}
                width={120}
                align="center"
                text={label}
                fontSize={12}
                fontStyle="bold"
                fill={colour}
                opacity={fade}
                shadowColor="#000"
                shadowBlur={4}
                shadowOpacity={fade}
              />
            )}
          </Group>
        );
      })}
    </>
  );

  if (bare) return body;
  return <Layer listening={false}>{body}</Layer>;
}
